import { Typography } from "@mui/material";
import { get } from "lodash";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import AppBar from "src/components/AppBar/AppBar";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import GuardedComponent from "src/components/GuardedComponent/GuardedComponent";
import Icon, { IconName } from "src/components/Icon/Icon";
import FilterContainer from "src/components/TableV2/FilterContainer";
import Title from "src/components/TableV2/Title";
import { UserRole } from "src/store/models/user.model";
import { hasAnyAuthority } from "src/util/roleAuthorization";
import { openUrl } from "src/util/util.navigation";
import { renderPrimaryContact } from "../../components/Table/columnRenders";
import SearchFieldTool from "../../components/TableV2/SearchField";
import DisTable from "../../components/TableV2/Table";
import * as S from "../../constants/StringConstants";
import { ICompany } from "../../store/models/company.model";
import { IRootState } from "../../store/reducers";
import { fetchCompanyList, removeCompany } from "../../store/reducers/companies";

interface ICompanyProps extends StateProps, DispatchProps, RouteComponentProps {}

interface ICompaniesState {
  deletedId: number | null;
}

class Companies extends React.Component<ICompanyProps, ICompaniesState> {
  constructor(props: ICompanyProps) {
    super(props);
    this.state = { deletedId: null };

    this.handleDeleteCompanyConfirm = this.handleDeleteCompanyConfirm.bind(this);
    this.handleDeleteCompanyCancel = this.handleDeleteCompanyCancel.bind(this);
  }

  handleDeleteCompanyPopup = (companyID: number) => {
    this.setState({ deletedId: companyID });
  };

  async handleDeleteCompanyConfirm() {
    const { deletedId } = this.state;
    if (deletedId !== null) {
      await this.props.removeCompany(deletedId);
      await this.props.fetchCompanyList();
      this.setState({ deletedId: null });
    }
  }

  handleDeleteCompanyCancel() {
    this.setState({ deletedId: null });
  }

  render() {
    const {
      auth,
      fetchCompanyList,
      companies: { map, projection, total, loading, ...listOptions },
    } = this.props;

    const isAdmin = hasAnyAuthority(auth.user, [UserRole.Admin]);

    const columns = [
      { key: "name", title: S.CO_COL_NAME, style: { minWidth: "15rem" } },
      { key: "affiliateName", title: S.CO_COL_AFFILIATE, style: { minWidth: "10rem" } },
      { key: "branch", title: S.CO_COL_BRANCH, style: { minWidth: "12rem" } },
      { key: "rbm", title: S.CO_COL_RBM, style: { minWidth: "8rem" } },
      { key: "phoneNumber", title: S.CO_COL_PHONE, style: { minWidth: "12rem" } },
      {
        key: "emailAddress",
        title: S.CO_COL_EMAIL,
        style: { paddingRight: "1rem" },
      },
      {
        key: "primaryContact",
        title: S.CO_COL_PRIMARY_CONTACT,
        render: (item: ICompany) => renderPrimaryContact(item.primaryContact),
        style: { minWidth: "15rem" },
      },
      { key: "companyID", title: S.CO_COL_COMPANY_ID, style: { minWidth: "11rem" } },
      { key: "externalID", title: S.CO_COL_EXTERNAL_ID, style: { minWidth: "11rem" } },
      ...(isAdmin
        ? [
            {
              key: "delete",
              title: S.CO_DELETE,
              style: { minWidth: "8rem" },
              render: (item: ICompany) => (
                <Icon
                  name={IconName.DeleteFileBlue}
                  size="16px"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.handleDeleteCompanyPopup(item.companyID);
                  }}
                />
              ),
            },
          ]
        : []),
    ];

    return (
      <div className="table-view-v2__content">
        <div className="table-view__header-buttons-container">
          <AppBar icon={IconName.Companies} title={S.CO_PAGE_TITLE} loading={loading}>
            <div className="table-view__buttons-container">
              <GuardedComponent
                authorizedRoles={[
                  UserRole.Admin,
                  UserRole.ProposalSpecialist,
                  UserRole.RegionalBranchManager,
                  UserRole.BranchManager,
                  UserRole.UnderwritingAssociate,
                ]}>
                <button className="side-nav__add-button" onClick={() => openUrl("/companies/new")}>
                  <Icon name={IconName.Plus} />
                  {S.NEW_COMPANY}
                </button>
              </GuardedComponent>
            </div>
          </AppBar>
        </div>

        <DisTable<ICompany>
          columns={columns}
          initialState={{ rows: 25, map, projection, total, ...listOptions }}
          onRowClick={(data: ICompany) => {
            this.props.history.push(`/companies/${data.companyID}`);
          }}
          onUpdate={async ({ page, rows, sort, query }) => {
            const result: any = await fetchCompanyList({ page, rows, sort, query });
            return {
              total: get(result, "value.data.totalRows") as number,
              data: get(result, "value.data.items") as ICompany[],
            };
          }}>
          <FilterContainer
            left={<Title text={S.CO_PAGE_TITLE} />}
            right={
              <>
                <SearchFieldTool />
              </>
            }
          />
        </DisTable>

        {this.state.deletedId !== null && (
          <PromptModal
            open
            title={S.CO_DELETE_CONFIRM}
            confirmText={S.DOC_DELETE_YES_BUTTON}
            rejectText={S.DOC_DELETE_NO_BUTTON}
            onConfirm={this.handleDeleteCompanyConfirm}
            onReject={this.handleDeleteCompanyCancel}>
            <Typography variant="h6">{S.CO_CONFIRM_DELETE}</Typography>
          </PromptModal>
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ companies, auth }: IRootState) => ({ companies, auth });
const mapDispatchToProps = { fetchCompanyList, removeCompany };

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(Companies);
